import { SECTIONS, Section } from '@anessa/shared-models';

/**
 * Single source of truth for which routes are hidden vs. required for multi-plant
 * projects. Read by `ui/sidebar/sidebar.ts`, `ui/command-palette/command-palette-pages.ts`,
 * and `ui/topbar/dropdown-menu/dropdown-menu-component.ts` so a route's multi-plant
 * visibility can't drift between the three surfaces.
 */

/** Single-plant-only features — hidden once a project spans multiple plants. */
export const MULTI_PLANT_HIDDEN_ROUTES: ReadonlySet<string> = new Set([
  '/flare-analysis',
  '/planning',
  '/approvisionnements',
  '/expeditions',
  '/corrections',
  '/matieres/gisements',
  '/parametrage/destinations',
  '/parametrage/contenants',
  '/parametrage/assumptions',
  '/parametrage/informations-site',
  '/incorporations/saisie',
  '/biodec',
  '/incorporations/dilutions',
  '/incorporations/detail',
  '/incorporations/suivi-hebdomadaire',
  '/objectives',
  '/ration/objectives',
  '/ration/loading-requirement',
  '/stocks/composition',
  '/stocks/statut',
  '/digestat',
  '/checklist-operateur',
  '/suivi-biologie',
  '/suivi-ration',
  '/synoptique',
  '/red',
  '/dashboard/operations-site/incorporations',
  '/dashboard/operations-site/biodec',
  '/dashboard/operations-site/stocks-niveaux',
  '/dashboard/operations-site/digestat',
  '/dashboard/operations-site/biologie-qualite',
  '/dashboard/operations-site/production',
  '/dashboard/operations-site/energie',
  // Opérations boards not built yet (greyed in the sidebar, no route yet).
  '/dashboard/operations-site/synthese-site',
  '/dashboard/operations-site/production-hebdomadaire',
  '/dashboard/operations-site/production-mensuelle',
  '/dashboard/operations-site/cockpit-production',
  '/dashboard/operations-site/flux-matieres',
  '/energies-utilites',
  '/suivi-site',
  '/suivi-transferts',
  '/ration-weekly-planning',
  '/motherdb',
  '/motherdb/sync-status',
  // Feedstock reference direct links (Base de données Intrants) — MotherDB
  // collections, hidden in multi-plant like the rest of the MotherDB browser.
  '/motherdb/collections/feedstock',
  '/motherdb/collections/wintrans_producers',
  '/motherdb/collections/wintrans_suppliers',
  '/motherdb/collections/wintrans_transporters',
  '/metrics-status',
]);

/** Multi-plant-only features — hidden unless a project spans multiple plants. */
export const MULTI_PLANT_REQUIRED_ROUTES: ReadonlySet<string> = new Set([
  '/project-settings',
]);

/**
 * Routes whose multi-plant content belongs to the whole project, so the backend
 * requires their section on **every** plant of a multi-plant project (e.g. the
 * project's own measures, `MetricScopeService`). With the section on only some
 * plants, the sidebar greys the route out, the palette leaves it out and the
 * route guard refuses it.
 */
export const MULTI_PLANT_EVERY_PLANT_SECTIONS: ReadonlyMap<string, Section> =
  new Map([['/insights', SECTIONS.MEASURES]]);

/** The section `route` needs on every plant of a multi-plant project, if any. */
export function everyPlantSectionFor(route: string): Section | undefined {
  return MULTI_PLANT_EVERY_PLANT_SECTIONS.get(route);
}

export function isHiddenForMultiPlant(route: string): boolean {
  return MULTI_PLANT_HIDDEN_ROUTES.has(route);
}

export function isRequiredForMultiPlant(route: string): boolean {
  return MULTI_PLANT_REQUIRED_ROUTES.has(route);
}

/** Whether `route` should be visible given the project's multi-plant status. */
export function isRouteVisibleForMultiPlant(
  route: string,
  isMultiPlant: boolean,
): boolean {
  if (isMultiPlant && isHiddenForMultiPlant(route)) return false;
  if (!isMultiPlant && isRequiredForMultiPlant(route)) return false;
  return true;
}
